import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  createAlphaSession,
  getAlphaMessages,
  getAlphaPendingConfirmations,
  getAlphaSessionsForContext,
} from 'services/alpha';
import {
  IAlphaPodContext,
  ICreateAlphaSessionResult,
  IAlphaSessionSummary,
  IAlphaStoredMessage,
} from 'services/alpha/types';
import { interruptedReplyText, parseStoredMessage } from 'services/alpha/parseStoredMessage';
import { expiredPendingTokens, toPendingConfirmations } from 'services/alpha/pendingConfirmations';
import { TIMED_OUT, settlesWithin } from 'services/alpha/settlesWithin';
import { CardDraft, clearCardDraft, writeCardDraft } from 'components/Alpha/cards/cardDraft';
import { AppDispatch, RootState } from 'store/index';
import { IAlphaMessage, IAlphaPendingConfirmation } from 'store/alpha/types';
import { latestRequestId } from 'store/alpha/requests';

interface IReloadWindow {
  watermark: number;
  selection: number | undefined;
  seenAtStart: IAlphaMessage[];
}

const openReloadWindow = (getState: () => RootState): IReloadWindow => ({
  watermark: latestRequestId(),
  selection: getState().alphaPod.selection,
  seenAtStart: getState().alphaPod.messages,
});

const reloadStillApplies = (
  getState: () => RootState,
  sessionId: number,
  opened: IReloadWindow,
): boolean => {
  const current = getState().alphaPod;
  return (
    latestRequestId() === opened.watermark &&
    current.sessionId === sessionId &&
    current.selection === opened.selection
  );
};

const SESSION_FAILURE = "Couldn't open Alpha for this ticket. Try again.";
const SESSIONS_LIST_FAILURE = "Couldn't load earlier chats for this ticket.";
const SELECT_SESSION_FAILURE = "Couldn't load that chat. Try again.";
const RELOAD_AFTER_VOICE_FAILURE = "Couldn't reload this chat after voice.";

export interface ICreateSessionFailure {
  message: string;
  unreachable?: boolean;
  sourceDisabled?: boolean;
}

const PSA_UNREACHABLE_FAILURE = "Couldn't reach your PSA right now. Try again.";
const SOURCE_DISABLED_REASON = 'source_disabled';

export const ThunkCreateAlphaSession = createAsyncThunk<
  ICreateAlphaSessionResult,
  IAlphaPodContext,
  { rejectValue: ICreateSessionFailure }
>('alpha/createSession', async (podContext, { rejectWithValue }) => {
  try {
    const res = await createAlphaSession(podContext);
    if (!res?.data) {
      return rejectWithValue({ message: SESSION_FAILURE });
    }
    return res.data;
  } catch (error) {
    const response = (
      error as { response?: { status?: number; data?: { message?: string; reason?: string } } }
    )?.response;
    const serverMessage = response?.data?.message;
    if (response?.status === 503) {
      return rejectWithValue({ message: serverMessage || PSA_UNREACHABLE_FAILURE, unreachable: true });
    }
    if (response?.status === 404 && response.data?.reason === SOURCE_DISABLED_REASON) {
      const shown = serverMessage?.trim() ?? '';
      return rejectWithValue({
        message: shown !== '' ? shown : SESSION_FAILURE,
        sourceDisabled: true,
      });
    }
    return rejectWithValue({ message: serverMessage || SESSION_FAILURE });
  }
});

export interface IGetSessionsForContextArgs {
  crmSourceId: number;
  externalId: string;
}

export const ThunkGetAlphaSessionsForContext = createAsyncThunk<
  IAlphaSessionSummary[],
  IGetSessionsForContextArgs,
  { rejectValue: string }
>('alpha/getSessionsForContext', async ({ crmSourceId, externalId }, { rejectWithValue }) => {
  try {
    const res = await getAlphaSessionsForContext(crmSourceId, externalId);
    return res?.data ?? [];
  } catch {
    return rejectWithValue(SESSIONS_LIST_FAILURE);
  }
});

export const PENDING_READ_TIMEOUT_MS = 10000;

const CARDS_READ_FAILURE = "Couldn't load this chat's open cards.";

const loadPendingConfirmations = async (
  sessionId: number,
): Promise<IAlphaPendingConfirmation[] | undefined> => {
  try {
    const res = await settlesWithin(
      getAlphaPendingConfirmations(sessionId),
      PENDING_READ_TIMEOUT_MS,
    );
    if (res === TIMED_OUT || !res) return undefined;
    expiredPendingTokens(res.data).forEach(token => clearCardDraft(token));
    return toPendingConfirmations(res.data);
  } catch {
    return undefined;
  }
};

export const mapStoredToMessages = (stored: IAlphaStoredMessage[]): IAlphaMessage[] => {
  const mapped = stored
    .filter(m => m.role !== 'tool' && !m.voice?.hidden)
    .map(storedMessage => {
      const parsed = parseStoredMessage(storedMessage.role, storedMessage.contentJson);
      return {
        role: storedMessage.role === 'user' ? ('user' as const) : ('assistant' as const),
        text: interruptedReplyText(storedMessage.synthetic, parsed.text) ?? parsed.text,
        toolCalls: parsed.toolCalls,
        serverId: storedMessage.id,
        expiredConfirmationToolName: parsed.expiredConfirmationToolName,
        ...(parsed.visuals ? { visuals: parsed.visuals } : {}),
        ...(parsed.handoff ? { handoff: parsed.handoff } : {}),
        ...(parsed.expiredConfirmationTitle
          ? { expiredConfirmationTitle: parsed.expiredConfirmationTitle }
          : {}),
        ...(storedMessage.channel === 'voice' ? { channel: 'voice' as const } : {}),
      };
    });
  return mapped
    .map((message, index) =>
      index === mapped.length - 1
        ? message
        : {
            ...message,
            expiredConfirmationToolName: undefined,
            expiredConfirmationTitle: undefined,
          },
    )
    .filter(
      m =>
        m.text ||
        (m.toolCalls && m.toolCalls.length > 0) ||
        m.expiredConfirmationToolName ||
        (m.visuals && m.visuals.length > 0) ||
        m.handoff,
    );
};

export const ThunkSelectAlphaSession = createAsyncThunk<
  { sessionId: number; messages: IAlphaMessage[] },
  number,
  { rejectValue: string }
>('alpha/selectSession', async (sessionId, { rejectWithValue }) => {
  try {
    const res = await getAlphaMessages(sessionId);
    const stored = res?.data ?? [];
    const messages = mapStoredToMessages(stored);
    return { sessionId, messages };
  } catch {
    return rejectWithValue(SELECT_SESSION_FAILURE);
  }
});

export interface IRestorePendingArgs {
  sessionId: number;
  restoreId: string;
}

export const ThunkRestorePendingConfirmations = createAsyncThunk<
  IAlphaPendingConfirmation[],
  IRestorePendingArgs,
  { rejectValue: string }
>('alpha/restorePendingConfirmations', async ({ sessionId }, { rejectWithValue }) => {
  const loaded = await loadPendingConfirmations(sessionId);
  return loaded ?? rejectWithValue(CARDS_READ_FAILURE);
});

export interface IRefreshAfterVoiceArgs {
  sessionId: number;
  drafts?: Record<string, CardDraft>;
}

export interface IRefreshAfterVoiceResult {
  sessionId: number;
  messages: IAlphaMessage[];
  seenAtStart: IAlphaMessage[];
  applyMessages: boolean;
  sentDuringRefresh: boolean;
  shownAtStart: string[];
  servedTokens: string[];
  pending: IAlphaPendingConfirmation[];
  imported: string[];
}

interface IKeptAfterRefreshArgs {
  applyMessages: boolean;
  sentDuringRefresh: boolean;
  shownAtStart: string[];
  shownNow: string[];
  servedTokens: string[];
  liveTokens: string[];
}

export const keptAfterRefresh = ({
  applyMessages,
  sentDuringRefresh,
  shownAtStart,
  shownNow,
  servedTokens,
  liveTokens,
}: IKeptAfterRefreshArgs): Set<string> => {
  if (applyMessages) return new Set(liveTokens);
  if (sentDuringRefresh) return new Set(shownNow);
  const served = new Set(servedTokens);
  const wasShown = new Set(shownAtStart);
  return new Set([
    ...shownNow.filter(token => !wasShown.has(token) || served.has(token)),
    ...liveTokens,
  ]);
};

export const ThunkRefreshAfterVoice = createAsyncThunk<
  IRefreshAfterVoiceResult,
  IRefreshAfterVoiceArgs,
  { state: RootState; rejectValue: string }
>('alpha/refreshAfterVoice', async ({ sessionId, drafts }, { getState, rejectWithValue }) => {
  const opened = openReloadWindow(getState);
  const sendsAtStart = getState().alphaPod.sendCount ?? 0;
  const shownAtStart = getState().alphaPod.pendingConfirmations.map(card => card.token);
  let stored: IAlphaStoredMessage[];
  let served: IAlphaPendingConfirmation[] | undefined;
  try {
    const [messages, pending] = await Promise.all([
      getAlphaMessages(sessionId),
      loadPendingConfirmations(sessionId),
    ]);
    stored = messages?.data ?? [];
    served = pending;
  } catch {
    return rejectWithValue(RELOAD_AFTER_VOICE_FAILURE);
  }
  if (!served) return rejectWithValue(RELOAD_AFTER_VOICE_FAILURE);
  const { alphaPod } = getState();
  const applyMessages = reloadStillApplies(getState, sessionId, opened);
  const sentDuringRefresh = (alphaPod.sendCount ?? 0) > sendsAtStart;
  const resolved = new Set(alphaPod.resolvedTokens ?? []);
  const live = served.filter(card => !resolved.has(card.token));
  const liveTokens = new Set(live.map(card => card.token));
  const shownNow =
    alphaPod.sessionId === sessionId ? alphaPod.pendingConfirmations.map(card => card.token) : [];
  const keptTokens = keptAfterRefresh({
    applyMessages,
    sentDuringRefresh,
    shownAtStart,
    shownNow,
    servedTokens: served.map(card => card.token),
    liveTokens: [...liveTokens],
  });
  const imported = Object.entries(drafts ?? {})
    .filter(([token]) => liveTokens.has(token) && keptTokens.has(token))
    .filter(([token, draft]) => writeCardDraft(token, draft))
    .map(([token]) => token);
  const known = [...shownNow, ...Object.keys(drafts ?? {}), ...served.map(card => card.token)];
  new Set(known).forEach(token => {
    if (!keptTokens.has(token)) clearCardDraft(token);
  });
  return {
    sessionId,
    messages: mapStoredToMessages(stored),
    seenAtStart: opened.seenAtStart,
    applyMessages,
    sentDuringRefresh,
    shownAtStart,
    servedTokens: served.map(card => card.token),
    pending: live,
    imported,
  };
});

export const selectAlphaSession =
  (sessionId: number) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    const action = await dispatch(ThunkSelectAlphaSession(sessionId));
    if (!ThunkSelectAlphaSession.fulfilled.match(action)) return false;
    await dispatch(
      ThunkRestorePendingConfirmations({ sessionId, restoreId: action.meta.requestId }),
    );
    return true;
  };

export const openAlphaPopupSession =
  (sessionId: number, podContext: IAlphaPodContext) =>
  async (dispatch: AppDispatch): Promise<void> => {
    if (await dispatch(selectAlphaSession(sessionId))) return;
    await dispatch(ThunkCreateAlphaSession(podContext));
  };

export interface IReloadMessagesResult {
  sessionId: number;
  messages: IAlphaMessage[];
  seenAtStart: IAlphaMessage[];
  apply: boolean;
}

export const ThunkReloadAlphaMessages = createAsyncThunk<
  IReloadMessagesResult,
  number,
  { state: RootState }
>('alpha/reloadMessages', async (sessionId, { getState }) => {
  const opened = openReloadWindow(getState);
  const res = await getAlphaMessages(sessionId);
  return {
    sessionId,
    messages: mapStoredToMessages(res?.data ?? []),
    seenAtStart: opened.seenAtStart,
    apply: reloadStillApplies(getState, sessionId, opened),
  };
});

export const refreshAfterVoice =
  (sessionId: number, drafts?: Record<string, CardDraft>) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const refreshed = await dispatch(ThunkRefreshAfterVoice({ sessionId, drafts }));
    if (!ThunkRefreshAfterVoice.fulfilled.match(refreshed)) {
      throw new Error(RELOAD_AFTER_VOICE_FAILURE);
    }
  };
